import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDataTable } from 'Src/feature/data-table/useDataTable';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { UsedModalSearchParamControl, useModalSearchParamControl } from 'Src/feature/modal/useModalControl';
import { usePaymentMethod } from 'Src/feature/payment/usePaymentMethod';
import { SearchComparator as Comp } from 'Src/models/queryOpts';

import { PayInOutTx } from './payInOutService';
import { usePayInOut } from './usePayInOut';

const { Form, Provider, Field } = getTypedFormComponents<FilterFormModel>();

class FilterFormModel {
  employeeId: PayInOutTx['employeeId'] | 'all' = 'all';
  paymentMethodId: PayInOutTx['paymentMethodId'] | 'all' = 'all';
  isPayIn: 'true' | 'false' | 'all' = 'all';

  constructor(part?: Partial<FilterFormModel>) {
    if (part) Object.assign(this, part);
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sectionId = 'usePayInOutList';

const usePayInOutList = (payInOutListModal: UsedModalSearchParamControl) => {
  const { t } = useTranslation();

  const payInOuts = usePayInOut.query.getAll();
  const payInOutReasonMap = usePayInOut.query.reason.map();
  const ccPaymentMethods = usePaymentMethod.query.ccMethods();
  const cashPaymentMethods = usePaymentMethod.query.cashMethods();
  const { data: paymentMethodMap } = usePaymentMethod.query.map();
  const employeeGetAll = useEmployee.query.getAll();
  const employeeGetAllMap = useEmployee.query.map();

  const [payInOutTxDetails, setPayInOutTxDetails] = useState<PayInOutTx | undefined>(undefined);
  const payInOutDetailsModal = useModalSearchParamControl({ value: 'pay-in-out-details', onClose: () => void loadPayInOuts() });
  const table = useDataTable({ data: payInOuts.data?.items });

  const tableFilterForm = useForm<FilterFormModel>({
    defaultValues: { ...new FilterFormModel() },
  });

  const onOpen = () => {
    payInOutListModal.open();
    void loadPayInOuts();
  };

  const onClose = () => {
    payInOutListModal.close();
  };

  const onFilter = async (field: keyof FilterFormModel, value: FilterFormModel[keyof FilterFormModel]) => {
    // Declare a variable to hold the processed filter value, initializing it with the raw input.
    // ??? looks like we need to convert 'true'/'false' strings to boolean true/false for filtering
    let filterVal: boolean | string | number = value;
    if (typeof value === 'string' && value === 'true') {
      filterVal = true;
    } else if (typeof value === 'string' && value === 'false') {
      filterVal = false;
    }
    // Ensure a filter configuration for this field exists in the data table store.
    table.store.filter.findOrCreate(field, { value: undefined });
    // Check if the user selected the 'all' option, which signifies no filtering on this field.
    // Otherwise, enable the filter, set its operator to 'equals', and apply the processed value.
    if (value === 'all') table.store.filter.updateOne(field, { isDisabled: true });
    else table.store.filter.updateOne(field, { isDisabled: false, operator: Comp.eq, value: filterVal });
    // - this was done after filtering so to avoid filtering and being on a page with no results.
    // - do this agnostic of active filter state as a filter change should always be a pager reset 
    table.store.pager.changePage(1);
  };

  const paymentMethodOptions = [
    { value: 'all', label: t('payInOut.labels.allPaymentMethods') },
    ...ccPaymentMethods.data?.map(({ id, name }) => ({ value: id, label: name })) ?? [],
    ...cashPaymentMethods.data?.map(({ id, name }) => ({ value: id, label: name })) ?? [],
  ];

  const typeOptions = [{ value: 'all', label: t('payInOut.labels.allTypes') }, { value: 'true', label: t('payInOut.labels.payIn') }, { value: 'false', label: t('payInOut.labels.payOut') }];

  const employeeIdsInLog = useMemo(() =>
    new Set(payInOuts.data?.items?.map((tx) => tx.employeeId)), [payInOuts.data?.items]);

  const employeesInLog = useMemo(() =>
    employeeGetAll.data?.filter((employee) => employeeIdsInLog.has(employee.id)), [employeeGetAll.data, employeeIdsInLog]);

  const employeeOptions = [
    { value: 'all', label: t('payInOut.labels.allEmployees') },
    ...(employeesInLog?.map((employee) => ({ value: employee.id, label: t('labels.util.employeeFullName', employee) })) ?? []),
  ];

  const loadPayInOuts = async () => {
    await payInOuts.refetch();
  };

  table.store.filter.createMany([
    { field: 'isPayIn', value: undefined, isDisabled: true },
    { field: 'paymentMethodId', value: undefined, isDisabled: true },
    { field: 'employeeId', value: undefined, isDisabled: true },
  ]);

  useEffect(() => {
    if (payInOutListModal.isOpen) {
      void loadPayInOuts();
      tableFilterForm.action.reset();
      table.store.filter.updateMany([
        { field: 'isPayIn', value: undefined, isDisabled: true },
        { field: 'paymentMethodId', value: undefined, isDisabled: true },
        { field: 'employeeId', value: undefined, isDisabled: true },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payInOutListModal.isOpen]);

  return {
    modal: {
      details: payInOutDetailsModal,
    },
    table: {
      ...table,
      pagination: table.store.data.all.length > 15 ? undefined : <></>,
      isProcessing: payInOuts.isLoading,
    },
    form: tableFilterForm,
    onOpen,
    onClose,
    onFilter,
    options: {
      paymentMethods: paymentMethodOptions,
      types: typeOptions,
      employees: employeeOptions,
    },
    payInOutTxDetails,
    setPayInOutTxDetails,
    employeeMap: employeeGetAllMap.data,
    paymentMethodMap: paymentMethodMap,
    payInOutReasonMap: payInOutReasonMap.data,
  };
};

export { Field, Form, Provider, usePayInOutList };
